
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, ChevronLeft, ChevronRight, Database, Sparkles, BarChart3 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface WorkflowDataViewerProps {
  runId: string;
  dataType: 'raw' | 'cleaned' | 'predicted';
}

const COLUMN_ORIGIN_MAP: Record<string, { label: string; icon: typeof Database; color: string }> = {
  raw: { label: 'Original CRM Value', icon: Database, color: 'text-muted-foreground' },
  cleaned: { label: 'Cleaned by AI', icon: Sparkles, color: 'text-green-600' },
  predicted: { label: 'Predicted by AI', icon: BarChart3, color: 'text-blue-600' },
};

export const WorkflowDataViewer = ({ runId, dataType }: WorkflowDataViewerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: rows, isLoading } = useQuery({
    queryKey: ['workflow-data-viewer', runId, dataType, page],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_data')
        .select('*')
        .eq('workflow_run_id', runId)
        .eq('data_type', dataType)
        .order('row_index')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: totalCount } = useQuery({
    queryKey: ['workflow-data-count', runId, dataType],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('workflow_data')
        .select('*', { count: 'exact', head: true })
        .eq('workflow_run_id', runId)
        .eq('data_type', dataType);
      if (error) return 0;
      return count || 0;
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading data…</p>;
  }

  if (!rows?.length) {
    return (
      <div className="py-12 text-center">
        <Database className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground">No {dataType} data available for this run.</p>
      </div>
    );
  }

  // Separate regular columns from AI-generated columns (prefixed with _)
  const allKeys = Object.keys(rows[0].row_data as Record<string, unknown>);
  const regularColumns = allKeys.filter(k => !k.startsWith('_'));
  const aiColumns = allKeys.filter(k => k.startsWith('_'));

  const filteredRows = searchQuery
    ? rows.filter(r => JSON.stringify(r.row_data).toLowerCase().includes(searchQuery.toLowerCase()))
    : rows;

  const totalPages = Math.ceil((totalCount || 0) / pageSize);

  const getColumnLabel = (col: string) => {
    if (col.startsWith('_')) {
      return col.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    return col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getColumnOriginBadge = (col: string) => {
    if (col.startsWith('_')) {
      if (col.includes('prediction') || col.includes('score') || col.includes('confidence') || col.includes('lead_score')) {
        return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-blue-600 border-blue-200 bg-blue-50">Predicted by AI</Badge>;
      }
      if (col.includes('cleaned') || col.includes('ai_notes')) {
        return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-600 border-green-200 bg-green-50">Cleaned by AI</Badge>;
      }
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-purple-600 border-purple-200 bg-purple-50">AI Generated</Badge>;
    }
    return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">Original CRM</Badge>;
  };

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      const str = JSON.stringify(value);
      return str.length > 80 ? str.slice(0, 77) + '…' : str;
    }
    return String(value);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">{totalCount} records</Badge>
          {dataType === 'raw' && <span className="text-sm text-muted-foreground">Original data from your CRM</span>}
          {dataType === 'cleaned' && <span className="text-sm text-muted-foreground">Data after AI cleaning and normalization</span>}
          {dataType === 'predicted' && <span className="text-sm text-muted-foreground">Data enriched with AI predictions</span>}
        </div>
        <div className="flex items-center gap-2 max-w-xs">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search records…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
            className="h-8"
          />
        </div>
      </div>

      <ScrollArea className="w-full">
        <div className="min-w-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                {regularColumns.map(col => (
                  <TableHead key={col} className="whitespace-nowrap">
                    <div className="space-y-0.5">
                      <span>{getColumnLabel(col)}</span>
                      <div>{getColumnOriginBadge(col)}</div>
                    </div>
                  </TableHead>
                ))}
                {aiColumns.map(col => (
                  <TableHead key={col} className="whitespace-nowrap">
                    <div className="space-y-0.5">
                      <span className="text-blue-600">{getColumnLabel(col)}</span>
                      <div>{getColumnOriginBadge(col)}</div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="text-muted-foreground text-xs">{row.row_index + 1}</TableCell>
                  {regularColumns.map(col => (
                    <TableCell key={col} className="max-w-[200px] truncate text-sm">
                      {formatCellValue((row.row_data as Record<string, unknown>)[col])}
                    </TableCell>
                  ))}
                  {aiColumns.map(col => (
                    <TableCell key={col} className="max-w-[200px] truncate text-sm text-blue-600">
                      {formatCellValue((row.row_data as Record<string, unknown>)[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
